import {
  FlowModuleItemType,
  ModuleIOValueTypeEnum,
} from "@packages/flow-core/module";
import { NodeProps } from "reactflow";
import NodeCard from "../NodeCard";
import styles from "./index.less";
import { Button, Divider, Tooltip, message } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  EditInputFieldMap,
  EditNodeFieldType,
  FlowNodeInputItemType,
} from "@packages/flow-core/module/node/types";
import FieldEditModal from "../render/FieldEditModal";
import { memo, useCallback, useMemo, useState } from "react";
import {
  FlowNodeInputTypeEnum,
  FlowNodeOutputTypeEnum,
} from "@packages/flow-core/module/node/constants";
import { useFlow } from "../FlowEdit/useFlow";
import SourceHandle from "../SourceHandle";
import { FlowValueTypeMap } from "../../../constants/modules/dataType";

export default memo(function NodePluginInput({
  data,
  selected,
}: NodeProps<FlowModuleItemType>) {
  const { moduleId, inputs } = data;
  const [open, setOpen] = useState(false);
  const {
    addModuleInput,
    deleteModuleInput,
    updateModuleInput,
    addModuleOutput,
    deleteModuleOutput,
    updateModuleOutput,
    delEdge,
    getAllEdges,
  } = useFlow();

  const [editFieldValue, setEditFieldValue] = useState<EditNodeFieldType>({});

  const createEditField: EditInputFieldMap = {
    key: true,
    name: true,
    description: true,
    required: true,
    dataType: true,
    defaultValue: true,
  };

  const limitedDataTypeSelectList = useMemo(
    () =>
      Object.values(FlowValueTypeMap)
        .filter(
          (item) =>
            item?.value === ModuleIOValueTypeEnum.string ||
            item?.value === ModuleIOValueTypeEnum.number ||
            item?.value === ModuleIOValueTypeEnum.boolean ||
            item?.value === ModuleIOValueTypeEnum.any
        )
        .map((item) => ({
          label: item!.label,
          value: item!.value,
        })),
    []
  );

  const getInputTypeByValueType = useCallback(
    (valueType: `${ModuleIOValueTypeEnum}`) => {
      switch (valueType) {
        case ModuleIOValueTypeEnum.string: {
          return FlowNodeInputTypeEnum.textarea;
        }
        case ModuleIOValueTypeEnum.number: {
          return FlowNodeInputTypeEnum.numberInput;
        }
        case ModuleIOValueTypeEnum.boolean: {
          return FlowNodeInputTypeEnum.switch;
        }
        default: {
          // 其他的回滚
          return FlowNodeInputTypeEnum.textarea;
        }
      }
    },
    []
  );

  return (
    <>
      <NodeCard minWidth={300} selected={selected} forbitMenu {...data}>
        <Divider style={{ margin: 0 }} />
        <div className={styles.content}>
          <div className={styles.right}>
            {inputs.map((input) => {
              return (
                <div
                  key={input.key}
                  className={`${styles["mb-7"]} ${styles["pos-rel"]}`}
                >
                  <DeleteOutlined
                    onClick={() => {
                      deleteModuleInput(moduleId, input.key);
                      deleteModuleOutput(moduleId, input.key);
                    }}
                  />
                  <SettingOutlined
                    className={styles["ml-4"]}
                    onClick={() => {
                      setEditFieldValue({
                        valueType: input.valueType,
                        required: input.required,
                        label: input.label,
                        key: input.key,
                        description: input.description,
                        defaultValue: input.value as string,
                      });
                      setOpen(true);
                    }}
                  />
                  {input.description && (
                    <Tooltip title={input.description}>
                      <QuestionCircleOutlined className={styles["ml-4"]} />
                    </Tooltip>
                  )}
                  <span className={styles["ml-4"]}>{input.label}</span>
                  {input.required && <span className={styles.required}>*</span>}
                  <SourceHandle
                    handleKey={input.key}
                    valueType={input.valueType}
                    moduleId={moduleId}
                  />
                </div>
              );
            })}
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setEditFieldValue({});
                setOpen(true);
              }}
            >
              添加输入参数
            </Button>
          </div>
        </div>
      </NodeCard>
      <FieldEditModal
        externalDataTypeSelectList={limitedDataTypeSelectList}
        field={createEditField}
        onClose={() => {
          setOpen(false);
        }}
        data={editFieldValue}
        onConfirm={(data) => {
          const newInput: FlowNodeInputItemType = {
            type: getInputTypeByValueType(data.valueType ?? "string"),
            valueType: data.valueType ?? "string",
            key: data.key!,
            label: data.label!,
            description: data.description ?? "",
            value: data.defaultValue ?? "",
            required: data.required,
            showTargetInApp: true,
            showTargetInPlugin: true,
            ...(data.inputType === FlowNodeInputTypeEnum.addInputParam
              ? {
                  editField: {
                    key: true,
                    name: true,
                    description: true,
                    required: true,
                    dataType: true,
                    defaultValue: true,
                  },
                  defaultEditField: {
                    label: "",
                    key: "",
                    description: "",
                    valueType: ModuleIOValueTypeEnum.string,
                    required: true,
                    defaultValue: "",
                  },
                }
              : {}),
          };
          const newOutput = {
            key: data.key!,
            valueType: data.valueType!,
            label: data.label!,
            type: FlowNodeOutputTypeEnum.source,
            edit: true,
            targets: [],
          };
          if (Object.keys(editFieldValue).length !== 0) {
            if (editFieldValue.key !== newInput.key) {
              const existed = inputs.find((item) => item.key === newInput.key);
              if (existed) {
                message.warning("字段Key不可重复");
                return;
              }
            }
            if (editFieldValue.valueType !== newInput.valueType) {
              // 删除已经绘制的线
              getAllEdges()
                .filter(
                  (edge) =>
                    edge.source === moduleId &&
                    edge.sourceHandle === newInput.key
                )
                .forEach((edge) => {
                  delEdge(edge.id);
                });
            }
            updateModuleInput(moduleId, editFieldValue.key!, newInput);
            updateModuleOutput(moduleId, editFieldValue.key!, newOutput);
          } else {
            const existed = inputs.find((item) => item.key === newInput.key);
            if (existed) {
              message.warning("字段Key不可重复");
              return;
            }
            addModuleInput(moduleId, newInput);
            addModuleOutput(moduleId, newOutput);
          }
          setOpen(false);
        }}
        visible={open}
      />
    </>
  );
});
